import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import api, { fmtError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, FileText, RefreshCw } from "lucide-react";

const METRICS = [
  { key: "total", label: "Total Recipients", filter: "all", cls: "text-foreground" },
  { key: "sent", label: "Sent", filter: "sent", cls: "text-sky-700" },
  { key: "delivered", label: "Delivered", filter: "delivered", cls: "text-emerald-700" },
  { key: "failed", label: "Failed", filter: "failed", cls: "text-red-600" },
  { key: "pending", label: "Pending", filter: "pending", cls: "text-amber-600" },
  { key: "bounced", label: "Bounced", filter: "bounced", cls: "text-rose-700" },
  { key: "opened", label: "Opened", filter: "opened", cls: "text-emerald-700" },
  { key: "not_opened", label: "Not Opened", filter: "not_opened", cls: "text-zinc-600" },
  { key: "clicked", label: "Clicked", filter: "clicked", cls: "text-primary" },
  { key: "unsubscribed", label: "Unsubscribed", filter: "unsubscribed", cls: "text-orange-700" },
];

const FILTERS = ["all", "sent", "delivered", "failed", "bounced", "opened", "not_opened", "clicked", "unsubscribed"];
const FILTER_LABEL = { all: "All", sent: "Sent", delivered: "Delivered", failed: "Failed", bounced: "Bounced", opened: "Opened", not_opened: "Not Opened", clicked: "Clicked", unsubscribed: "Unsubscribed" };

const fmtT = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtN = (v) => (v === null || v === undefined ? "—" : v.toLocaleString());
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

function rowMatches(r, f) {
  switch (f) {
    case "all": return true;
    case "sent": return r.delivery_status !== "failed";
    case "delivered": return r.delivery_status === "delivered";
    case "failed": return r.delivery_status === "failed";
    case "bounced": return r.delivery_status === "bounced";
    case "opened": return !!r.opened_at;
    case "not_opened": return !r.opened_at && r.delivery_status !== "failed";
    case "clicked": return !!r.clicked_at;
    case "unsubscribed": return !!r.unsubscribed_at;
    default: return true;
  }
}

export default function OutlookReportDialog({ batch, open, onOpenChange }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    if (!batch?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/marketing/outlook/batches/${batch.id}/report`);
      setReport(data);
    } catch (e) { toast.error(fmtError(e)); } finally { setLoading(false); }
  }, [batch?.id]);

  useEffect(() => { if (open) { setFilter("all"); load(); } }, [open, load]);

  const scanBounces = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/marketing/outlook/scan-bounces");
      if (data.mail_read_granted === false) {
        toast.info("Bounce detection needs the Mail.Read permission in Azure — ask your IT admin to add it to the app registration (with admin consent).", { duration: 9000 });
      } else {
        toast.success(`Bounce scan complete — ${data.bounced_marked} new bounce${data.bounced_marked === 1 ? "" : "s"} found`);
        load();
      }
    } catch (e) { toast.error(fmtError(e)); } finally { setScanning(false); }
  };

  const rows = useMemo(() => (report?.recipients || []).filter((r) => rowMatches(r, filter)), [report, filter]);

  const exportRows = (rowsArr) => rowsArr.map((r) => ({
    Email: r.email, "Sent Time": r.sent_at || "", "Delivery Status": r.delivery_status,
    Opened: r.opened_at ? "Yes" : "No", "Open Time": r.opened_at || "",
    Clicked: r.clicked_at ? "Yes" : "No", "Click Time": r.clicked_at || "",
    Unsubscribed: r.unsubscribed_at ? "Yes" : "No", "Bounce/Failure Reason": r.reason || "",
  }));

  const slug = (batch?.subject || "campaign").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows(rows));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Campaign Report");
    XLSX.writeFile(wb, `report-${slug}.xlsx`);
  };

  const exportCsv = () => {
    const ws = XLSX.utils.json_to_sheet(exportRows(rows));
    const csv = XLSX.utils.sheet_to_csv(ws);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `report-${slug}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPdf = () => {
    const c = report.counts, rt = report.rates;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked — allow popups to download the PDF"); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>${batch?.subject || "Campaign report"}</title>
<style>body{font-family:Arial;font-size:12px;padding:24px}h1{font-size:18px}table{border-collapse:collapse;width:100%;margin-top:12px}
td,th{border:1px solid #ddd;padding:5px 7px;text-align:left;font-size:11px}th{background:#f3f4f6}.m{display:inline-block;margin:4px 12px 4px 0}</style></head><body>
<h1>${batch?.subject || "Campaign report"}</h1>
<p>${METRICS.map((m) => `<span class="m"><strong>${m.label}:</strong> ${fmtN(c[m.key])}</span>`).join("")}</p>
<p><span class="m"><strong>Delivery Rate:</strong> ${fmtPct(rt.delivery_rate)}</span><span class="m"><strong>Open Rate:</strong> ${fmtPct(rt.open_rate)}</span>
<span class="m"><strong>Click Rate:</strong> ${fmtPct(rt.click_rate)}</span><span class="m"><strong>Bounce Rate:</strong> ${fmtPct(rt.bounce_rate)}</span>
<span class="m"><strong>Unsubscribe Rate:</strong> ${fmtPct(rt.unsubscribe_rate)}</span></p>
<table><tr><th>Email</th><th>Sent Time</th><th>Status</th><th>Open Time</th><th>Click Time</th><th>Reason</th></tr>
${rows.map((r) => `<tr><td>${r.email}</td><td>${fmtT(r.sent_at)}</td><td>${r.delivery_status}</td><td>${fmtT(r.opened_at)}</td><td>${fmtT(r.clicked_at)}</td><td>${r.reason || ""}</td></tr>`).join("")}
</table><script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="outlook-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Campaign Report</DialogTitle>
          <DialogDescription className="truncate">{batch?.subject}</DialogDescription>
        </DialogHeader>
        {loading || !report ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5" data-testid="outlook-report-metrics">
              {METRICS.map((m) => (
                <button key={m.key} type="button" onClick={() => setFilter(m.filter)}
                  className={`rounded-2xl border px-2 py-2 text-left transition-colors ${filter === m.filter ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"}`}
                  data-testid={`outlook-report-metric-${m.key}`}>
                  <p className={`text-lg font-extrabold leading-none ${m.cls}`}>{fmtN(report.counts[m.key])}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{m.label}</p>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5" data-testid="outlook-report-rates">
              <span>Delivery Rate: <strong>{fmtPct(report.rates.delivery_rate)}</strong></span>
              <span>Open Rate: <strong>{fmtPct(report.rates.open_rate)}</strong></span>
              <span>Click Rate: <strong>{fmtPct(report.rates.click_rate)}</strong></span>
              <span>Bounce Rate: <strong>{fmtPct(report.rates.bounce_rate)}</strong></span>
              <span>Unsubscribe Rate: <strong>{fmtPct(report.rates.unsubscribe_rate)}</strong></span>
            </p>
            {!report.delivery_tracking && (
              <p className="text-[11px] rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5" data-testid="outlook-report-mailread-note">
                Delivered / Bounced show "—" until the <strong>Mail.Read</strong> permission is added to the Azure app. Then hit "Check bounces".
              </p>
            )}
            {!report.engagement_tracking && (
              <p className="text-[11px] rounded-xl bg-muted/40 border border-border/60 text-muted-foreground px-3 py-1.5" data-testid="outlook-report-legacy-note">
                Opened / Clicked / Unsubscribed tracking applies to campaigns sent after the tracking update — this batch predates it.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              {FILTERS.map((f) => (
                <button key={f} type="button" onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${filter === f ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border/70 hover:border-primary/50"}`}
                  data-testid={`outlook-report-filter-${f}`}>
                  {FILTER_LABEL[f]}
                </button>
              ))}
              <span className="flex-1" />
              <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs" disabled={scanning} onClick={scanBounces} data-testid="outlook-report-scan-bounces">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${scanning ? "animate-spin" : ""}`} /> Check bounces
              </Button>
            </div>
            <div className="rounded-2xl border border-border/60 overflow-hidden">
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-xs" data-testid="outlook-report-table">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold">Email</th>
                      <th className="text-left px-2 py-2 font-bold">Sent Time</th>
                      <th className="text-left px-2 py-2 font-bold">Status</th>
                      <th className="text-left px-2 py-2 font-bold">Opened</th>
                      <th className="text-left px-2 py-2 font-bold">Clicked</th>
                      <th className="text-left px-2 py-2 font-bold">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {rows.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground" data-testid="outlook-report-empty">No recipients match "{FILTER_LABEL[filter]}".</td></tr>
                    ) : rows.slice(0, 1000).map((r, i) => (
                      <tr key={i} data-testid={`outlook-report-row-${i}`}>
                        <td className="px-3 py-1.5 font-mono truncate max-w-[200px]">{r.email}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtT(r.sent_at)}</td>
                        <td className={`px-2 py-1.5 font-semibold capitalize ${r.delivery_status === "failed" ? "text-red-600" : r.delivery_status === "bounced" ? "text-rose-700" : "text-emerald-700"}`}>{r.delivery_status}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtT(r.opened_at)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{fmtT(r.clicked_at)}</td>
                        <td className="px-2 py-1.5 text-muted-foreground max-w-[220px] truncate" title={r.reason}>{r.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 1000 && <p className="text-[10px] text-muted-foreground p-2">Showing first 1,000 — export for the full list.</p>}
            </div>
            <div className="flex flex-wrap justify-end gap-1.5">
              <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs" onClick={exportExcel} data-testid="outlook-report-export-excel"><Download className="h-3.5 w-3.5 mr-1" /> Export Excel</Button>
              <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs" onClick={exportCsv} data-testid="outlook-report-export-csv"><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</Button>
              <Button size="sm" variant="outline" className="rounded-xl h-8 text-xs" onClick={exportPdf} data-testid="outlook-report-export-pdf"><Download className="h-3.5 w-3.5 mr-1" /> Download PDF</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
